/**
 * Concession Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useConcessionDetail hook.
 */

import { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  MessageSquare,
  Paperclip,
  History,
  ShieldCheck,
} from 'lucide-react';
import {
  useConcessionDetail,
  STATUS_COLORS,
  RISK_COLORS,
  DeviationDetails,
  ReviewDecision,
  CommentsTab,
  AttachmentsTab,
  HistoryTab,
  SummaryCard,
  ActionDialogs,
  requestTypeLabel,
} from './concession-detail';
import { platformSettingsAPI, QualityTabsVisibility } from '../lib/api';

export default function ConcessionDetail() {
  const {
    // Navigation
    navigate,

    // User info
    isSupplier,
    isCustomer,

    // Loading states
    loading,
    error,
    actionLoading,
    commentLoading,

    // Data
    concession,
    comments,
    actionLogs,
    documents,
    uploading,
    currentUserId,

    // Dialog state
    dialogs,
    setDialogs,
    rejectionReason,
    setRejectionReason,
    approvalForm,
    setApprovalForm,
    approvalErrors,

    // Comment state
    newComment,
    setNewComment,
    isInternalComment,
    setIsInternalComment,
    mentionOptions,
    mentionedUserIds,
    setMentionedUserIds,
    mentionedDepartments,
    setMentionedDepartments,

    // Actions
    handleSubmit,
    handleMarkUnderReview,
    handleApprove,
    handleReject,
    handleClose,
    handleAcceptRisk,
    handleAddComment,
    handleUploadDocument,
    handleDeleteDocument,
  } = useConcessionDetail();

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if concessions tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if concessions tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.concessions) {
      navigate('/concessions', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.concessions, navigate]);

  const getStatusBadge = (status: string) => {
    const config = STATUS_COLORS[status];
    return <Badge variant="outline" className={config?.className || ''}>{config?.label || status}</Badge>;
  };

  const getRiskBadge = (riskLevel: string) => {
    const config = RISK_COLORS[riskLevel];
    return <Badge variant="outline" className={config?.className || ''}>{config?.label || riskLevel}</Badge>;
  };

  // Show loading while checking tab settings or loading concession
  if (!tabSettingsLoaded || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If concessions tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.concessions) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (error || !concession) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Concession not found'}</p>
          <button
            onClick={() => navigate('/concessions')}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Back to Concessions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/concessions')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{concession.concession_number}</h1>
            {/* CR-6510: CE or PE — the reviewer decides differently on each. */}
            <Badge variant="outline" data-testid="concession-request-type">{requestTypeLabel(concession)}</Badge>
            {getStatusBadge(concession.status)}
            {getRiskBadge(concession.risk_level)}
          </div>
          <p className="text-muted-foreground mt-1">{concession.title}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {isSupplier && concession.status === 'draft' && (
            <Button onClick={handleSubmit} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Submit for Review
            </Button>
          )}

          {isCustomer && concession.status === 'submitted' && (
            <Button variant="outline" onClick={handleMarkUnderReview} disabled={actionLoading}>
              <Clock className="h-4 w-4 mr-2" />
              Mark Under Review
            </Button>
          )}

          {isCustomer && ['submitted', 'under_review'].includes(concession.status) && (
            <>
              <Button variant="outline" onClick={() => setDialogs(prev => ({ ...prev, reject: true }))} disabled={actionLoading}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button onClick={() => setDialogs(prev => ({ ...prev, approve: true }))} disabled={actionLoading}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve
              </Button>
            </>
          )}

          {isCustomer && concession.status === 'approved' && !concession.risk_accepted && (
            <Button onClick={handleAcceptRisk} disabled={actionLoading}>
              <ShieldCheck className="h-4 w-4 mr-2" />
              Accept Risk
            </Button>
          )}

          {/* CR-7454: closing is the customer's decision (Helix staff count as
              customer here); the API refuses a supplier with 403. */}
          {isCustomer && ['approved', 'rejected'].includes(concession.status) && (
            <Button variant="outline" onClick={handleClose} disabled={actionLoading}>
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          <DeviationDetails concession={concession} />
          <ReviewDecision concession={concession} />

          {/* Tabs for Comments, Attachments and History */}
          <Tabs defaultValue="comments" className="space-y-4">
            <TabsList>
              <TabsTrigger value="comments">
                <MessageSquare className="h-4 w-4 mr-2" />
                Comments ({comments.length})
              </TabsTrigger>
              <TabsTrigger value="attachments">
                <Paperclip className="h-4 w-4 mr-2" />
                Attachments ({documents.length})
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-2" />
                History ({actionLogs.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="comments">
              <CommentsTab
                comments={comments}
                newComment={newComment}
                isInternalComment={isInternalComment}
                commentLoading={commentLoading}
                isCustomer={isCustomer}
                onCommentChange={setNewComment}
                onInternalChange={setIsInternalComment}
                mentionOptions={mentionOptions}
                mentionedUserIds={mentionedUserIds}
                mentionedDepartments={mentionedDepartments}
                onMentionedUsersChange={setMentionedUserIds}
                onMentionedDepartmentsChange={setMentionedDepartments}
                onAddComment={handleAddComment}
              />
            </TabsContent>

            <TabsContent value="attachments">
              <AttachmentsTab
                documents={documents}
                uploading={uploading}
                currentUserId={currentUserId}
                onUpload={handleUploadDocument}
                onDelete={handleDeleteDocument}
              />
            </TabsContent>

            <TabsContent value="history">
              <HistoryTab actionLogs={actionLogs} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Column - Summary */}
        <SummaryCard concession={concession} />
      </div>

      {/* Action Dialogs */}
      <ActionDialogs
        concession={concession}
        dialogs={dialogs}
        rejectionReason={rejectionReason}
        approvalForm={approvalForm}
        approvalErrors={approvalErrors}
        actionLoading={actionLoading}
        onDialogsChange={setDialogs}
        onRejectionReasonChange={setRejectionReason}
        onApprovalFormChange={setApprovalForm}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
